import { useState, useEffect, useRef } from 'react';
import {
    Upload01,
    SearchLg,
    Eye,
    Trash01,
    XCircle
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { trainingDocumentService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { TrainingDocument, CreateTrainingDocumentData, TrainingDocumentFilters, DocumentType, TrainingStatus } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const TrainingDocumentsPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [documents, setDocuments] = useState<TrainingDocument[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [documentTypeFilter, setDocumentTypeFilter] = useState<DocumentType | ''>('');
    const [statusFilter, setStatusFilter] = useState<TrainingStatus | ''>('');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<TrainingDocument | null>(null);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [uploadFormData, setUploadFormData] = useState<{
        document_type: DocumentType;
        dataset_name: string;
        dataset_version: string;
        notes: string;
    }>({
        document_type: 'bill_of_lading',
        dataset_name: 'default',
        dataset_version: '1.0',
        notes: '',
    });

    const documentTypeOptions = [
        { id: '', label: t('training.documentTypePlaceholder') },
        ...DOCUMENT_TYPES.map((dt) => ({ id: dt.key, label: dt.label })),
    ];

    const statusOptions = [
        { id: '', label: t('training.statusPlaceholder') },
        { id: 'pending', label: t('training.status.pending') },
        { id: 'processing', label: t('training.status.processing') },
        { id: 'completed', label: t('training.status.completed') },
        { id: 'failed', label: t('training.status.failed') },
    ];

    useEffect(() => {
        loadDocuments();
        // Poll for status updates
        const interval = setInterval(() => {
            loadDocuments();
        }, 2000);
        return () => clearInterval(interval);
    }, [searchTerm, documentTypeFilter, statusFilter]);

    const loadDocuments = () => {
        setIsLoading(true);
        const filters: TrainingDocumentFilters = {
            search: searchTerm || undefined,
            document_type: documentTypeFilter || undefined,
            training_status: statusFilter || undefined,
        };
        const filtered = trainingDocumentService.search(filters);
        setDocuments(filtered);
        setIsLoading(false);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        setUploadFiles([...uploadFiles, ...files]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadFiles([...uploadFiles, ...files]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadFiles(uploadFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (uploadFiles.length === 0) {
            alert(t('common.selectAtLeastOneFile'));
            return;
        }

        for (const file of uploadFiles) {
            const progressKey = file.name;
            setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));

            const data: CreateTrainingDocumentData = {
                file,
                document_type: uploadFormData.document_type,
                dataset_name: uploadFormData.dataset_name,
                dataset_version: uploadFormData.dataset_version,
                notes: uploadFormData.notes,
            };

            // Simulate upload progress
            for (let i = 0; i <= 100; i += 10) {
                await new Promise((resolve) => setTimeout(resolve, 100));
                setUploadProgress((prev) => ({ ...prev, [progressKey]: i }));
            }

            await trainingDocumentService.create(data);
        }

        setUploadFiles([]);
        setUploadProgress({});
        setIsUploadModalOpen(false);
        loadDocuments();
    };

    const handleOpenDetails = (doc: TrainingDocument) => {
        setSelectedDocument(doc);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedDocument(null);
    };

    const handleDelete = (id: string) => {
        if (confirm(t('modal.confirmDelete'))) {
            trainingDocumentService.delete(id);
            loadDocuments();
        }
    };

    const getStatusBadge = (status: TrainingStatus) => {
        switch (status) {
            case 'pending':
                return <Badge color="gray" size="sm">{t('training.status.pending')}</Badge>;
            case 'processing':
                return <Badge color="blue" size="sm">{t('training.status.processing')}</Badge>;
            case 'completed':
                return <Badge color="success" size="sm">{t('training.status.completed')}</Badge>;
            case 'failed':
                return <Badge color="error" size="sm">{t('training.status.failed')}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('training.title')}</h2>
                    <p className="text-tertiary mt-1">{t('training.description')}</p>
                </div>
                <Button
                    onClick={() => setIsUploadModalOpen(true)}
                    size="md"
                    color="primary"
                    iconLeading={Upload01}
                >
                    {t('training.uploadDocuments')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('training.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('training.documentTypePlaceholder')}
                        items={documentTypeOptions}
                        selectedKey={documentTypeFilter}
                        onSelectionChange={(key) => setDocumentTypeFilter(key as DocumentType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('training.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as TrainingStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Documents Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('training.title')} (${documents.length})`}
                    description={t('training.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('training.headers.fileName')} isRowHeader />
                        <Table.Head label={t('training.headers.documentType')} />
                        <Table.Head label={t('training.headers.status')} />
                        <Table.Head label={t('training.headers.uploadDate')} />
                        <Table.Head label={t('training.headers.extractionScore')} />
                        <Table.Head label={t('training.headers.validationScore')} />
                        <Table.Head label={t('training.headers.detectedSchema')} />
                        <Table.Head label={t('training.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {documents.map((doc) => (
                            <Table.Row key={doc.training_doc_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{doc.file_name}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">
                                        {DOCUMENT_TYPES.find((dt) => dt.key === doc.document_type)?.label || doc.document_type}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    {getStatusBadge(doc.training_status)}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {doc.created_at.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    {doc.extraction_score !== undefined ? (
                                        <span className="text-sm text-primary">
                                            {(doc.extraction_score * 100).toFixed(1)}%
                                        </span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {doc.validation_quality_score !== undefined ? (
                                        <span className="text-sm text-primary">
                                            {(doc.validation_quality_score * 100).toFixed(1)}%
                                        </span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {doc.detected_schema ? (
                                        <span className="text-sm text-tertiary font-mono">{doc.detected_schema}</span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenDetails(doc)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Eye}
                                        >
                                            {t('training.actions.viewDetails')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(doc.training_doc_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('training.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {documents.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            <DialogTrigger isOpen={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {t('training.uploadDocuments')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select
                                            label={t('training.modal.fields.documentType')}
                                            items={documentTypeOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={uploadFormData.document_type}
                                            onSelectionChange={(key) =>
                                                setUploadFormData({ ...uploadFormData, document_type: key as DocumentType })
                                            }
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Input
                                            label={t('training.modal.fields.datasetName')}
                                            value={uploadFormData.dataset_name}
                                            onChange={(value) => setUploadFormData({ ...uploadFormData, dataset_name: value })}
                                            isRequired
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('training.modal.fields.datasetVersion')}
                                        value={uploadFormData.dataset_version}
                                        onChange={(value) => setUploadFormData({ ...uploadFormData, dataset_version: value })}
                                        size="md"
                                    />

                                    {/* File Upload Area */}
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-brand bg-brand/10' : 'border-secondary'
                                            }`}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <Upload01 className="size-12 mx-auto mb-4 text-tertiary" />
                                        <p className="text-primary mb-2">{t('training.uploadArea')}</p>
                                        <p className="text-sm text-tertiary mb-4">{t('training.supportedFormats')}</p>
                                        <Button
                                            onClick={() => fileInputRef.current?.click()}
                                            size="md"
                                            color="secondary"
                                        >
                                            {t('common.selectFile')}
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Selected Files */}
                                    {uploadFiles.length > 0 && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">{t('common.selectedFiles')}</label>
                                            <div className="space-y-1">
                                                {uploadFiles.map((file, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between rounded-lg bg-secondary p-2"
                                                    >
                                                        <span className="text-sm text-primary">{file.name}</span>
                                                        <div className="flex items-center gap-2">
                                                            {uploadProgress[file.name] !== undefined && (
                                                                <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-brand transition-all"
                                                                        style={{ width: `${uploadProgress[file.name]}%` }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <button
                                                                onClick={() => handleRemoveFile(index)}
                                                                className="text-error hover:text-error_hover"
                                                            >
                                                                <XCircle className="size-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={() => {
                                            setIsUploadModalOpen(false);
                                            setUploadFiles([]);
                                            setUploadProgress({});
                                        }}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleUpload} size="md" color="primary" iconLeading={Upload01}>
                                        {t('common.upload')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Details Modal */}
            <DialogTrigger isOpen={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {t('training.modal.details')}
                                </h2>

                                {selectedDocument && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.fileName')}
                                                </label>
                                                <p className="text-primary">{selectedDocument.file_name}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.documentType')}
                                                </label>
                                                <p className="text-primary">
                                                    {DOCUMENT_TYPES.find((dt) => dt.key === selectedDocument.document_type)?.label ||
                                                        selectedDocument.document_type}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.status')}
                                                </label>
                                                <div>{getStatusBadge(selectedDocument.training_status)}</div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.extractionScore')}
                                                </label>
                                                <p className="text-primary">
                                                    {selectedDocument.extraction_score !== undefined
                                                        ? `${(selectedDocument.extraction_score * 100).toFixed(1)}%`
                                                        : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.validationScore')}
                                                </label>
                                                <p className="text-primary">
                                                    {selectedDocument.validation_quality_score !== undefined
                                                        ? `${(selectedDocument.validation_quality_score * 100).toFixed(1)}%`
                                                        : '-'}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-tertiary">
                                                    {t('training.modal.fields.detectedSchema')}
                                                </label>
                                                <p className="text-primary font-mono">
                                                    {selectedDocument.detected_schema || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseDetails} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
